<template>
  <div class="profession-card">
    <div class="profession-card__main" :class="[{ 'fw-bolder': top }]">
      <span class="profession-card__top" v-if="top">TOP {{ index }}</span>
      <span
        v-if="showRankBars && rankLevel && infTooltip"
        class="profession-card__bars"
        v-b-tooltip.hover.html.bottom="{ customClass: 'profession-info-tooltip' }"
        :title="infTooltip"
      >
        <qualification-level-bars :level="rankLevel" />
      </span>
      <span v-else-if="showRankBars && rankLevel" class="profession-card__bars">
        <qualification-level-bars :level="rankLevel" />
      </span>
      <span class="profession-card__title">{{ title }}</span>
    </div>

    <div class="profession-card__actions">
      <span v-if="regularedProfession" v-b-modal.modal-regulated-professions class="profession-card__warn">
        <b-icon aria-hidden="true" icon="exclamation-triangle" />
      </span>
      <div class="profession-card__btn-group">
        <button
          type="button"
          class="profession-card__count"
          :aria-label="translations[13500]"
          @click="searchProfession()"
        >
          {{ quantity }}
        </button>
        <b-dropdown
          :id="dropdownId"
          class="profession-card__menu"
          variant="link"
          no-caret
          right
          toggle-class="profession-card__chevron"
          menu-class="profession-card__dropdown"
          :toggle-attrs="{ 'aria-label': 'Aktionen', 'aria-controls': dropdownMenuId }"
        >
          <template #button-content>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down"><path d="m6 9 6 6 6-6"></path></svg>
          </template>
          <b-dropdown-item-button button-class="d-flex align-items-top text-nowrap" @click="searchProfession()">
            <b-icon aria-hidden="true" icon="list-ul" class="mr-2" />
            {{ translations[13500] }}
          </b-dropdown-item-button>
          <b-dropdown-item-button button-class="d-flex align-items-top text-nowrap" @click="searchProfessionMap()">
            <b-icon aria-hidden="true" icon="geo-alt" class="mr-2" />
            {{ translations[13501] }}
          </b-dropdown-item-button>
          <b-dropdown-item
            :href="isAuth ? `https://job-server.net/jobcentral/pdf?sid=${languageSid}&f=file&type=pdf&id=b${id}` : ''"
            v-if="pdf"
            v-b-modal="!isAuth ? 'not-auth-modal' : ''"
            class="d-flex align-items-top text-nowrap"
          >
            <b-icon aria-hidden="true" icon="file-earmark-pdf" class="mr-2" />
            {{ translations[10426] }}
            <span v-if="!isAuth" class="ml-auto" v-b-tooltip.hover title="Bitte registrieren, um diese Funktion zu nutzen.">
              <b-icon aria-hidden="true" icon="shield-lock-fill" />
            </span>
          </b-dropdown-item>
          <b-dropdown-item
            :href="isAuth ? onClickExcel : ''"
            target="_blank"
            v-b-modal="!isAuth ? 'not-auth-modal' : ''"
            class="d-flex align-items-top text-nowrap"
          >
            <b-icon aria-hidden="true" icon="file-earmark-excel" class="mr-2" />
            {{ translations[10449] }}
            <span v-if="!isAuth" class="ml-auto" v-b-tooltip.hover title="Bitte registrieren, um diese Funktion zu nutzen.">
              <b-icon aria-hidden="true" icon="shield-lock-fill" />
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { ProfessionModule } from '@/store/modules/profession';
import { JobType } from '@/types/ProfessionType';
import { AppModule } from '@/store/modules/app';
import { MapModule } from '@/store/modules/map';
import QualificationLevelBars from '@/components/Home/QualificationLevelBars.vue';

@Component({
  components: { QualificationLevelBars },
})
export default class ProfessionsItem extends Vue {
  @Prop({ default: false }) top!: boolean;

  @Prop({ default: 0 }) private index!: number;

  @Prop({ default: '' }) private title!: string;

  @Prop({ default: '' }) private name!: string | string[];

  @Prop({ default: 0 }) private quantity!: number;

  @Prop({ default: 0 }) private plt!: number;

  @Prop({ default: 0 }) private pdf!: number;

  @Prop({ default: 0 }) private id!: number;

  @Prop({ default: '' }) private sid!: string;

  @Prop({ default: '' }) private inf!: string | string[];

  @Prop({ default: null }) private rank!: string | number | null;

  @Prop({ default: false }) private showRankBars!: boolean;

  @Prop({ default: false }) isAuth!: boolean;

  @Prop({ default: false }) regularedProfession!: boolean;

  @Prop({ default: '' }) languageSid!: string;

  // BootstrapVue drops `$attrs` on the wrapper `<div class="dropdown">`, which
  // carries no role, so an `aria-label` there names nothing. `toggle-attrs` is
  // the documented way to reach the real toggle button, which already gets
  // `aria-haspopup="menu"` and a live `aria-expanded` from BootstrapVue.
  get dropdownId() {
    return `profession-actions-${this.id}`;
  }

  get dropdownMenuId() {
    return `${this.dropdownId}__menu`;
  }

  mounted() {
    // BootstrapVue renders the menu `<ul>` without an id and exposes no prop for
    // one, so `aria-controls` on the toggle would point at nothing. Setting it
    // here is safe: the id is not part of the menu's virtual DOM, so Vue never
    // overwrites or removes it on a later patch.
    const menu = this.$el.querySelector('.dropdown-menu');
    if (menu) menu.id = this.dropdownMenuId;
  }

  get translations() {
    return AppModule.translation;
  }

  get rankLevel(): 1 | 2 | 3 | null {
    const n = Number(this.rank);
    if (n === 1 || n === 2 || n === 3) return n as 1 | 2 | 3;
    return null;
  }

  get normalizedProfessionIds(): string[] {
    if (Array.isArray(this.id)) {
      return this.id.map((x) => String(x).trim()).filter((x) => x);
    }

    return String(this.id)
      .split('~')
      .map((x) => x.trim())
      .filter((x) => x);
  }

  get infTooltip(): string {
    if (!this.inf || this.inf === 'inf') return '';
    const infRaw = Array.isArray(this.inf) ? this.inf.join('<br />') : this.inf;
    if (!infRaw || infRaw === 'inf') return '';
    const items = infRaw.split('<br />').filter((item) => item.trim());
    const rawName = Array.isArray(this.name) ? this.name.join('<br />') : (this.name || this.title);
    const pureName = this.getPureName(rawName).replace(/\s*\|\|\|\s*/g, '<br />');
    const header = `<div style="font-weight: 600; margin-bottom: 8px;">${this.buildTooltipHeader(pureName)}</div>`;
    const listItems = items.map((item) => `<div style="display: flex; align-items: flex-start; margin-bottom: 4px;"><span style="margin-right: 8px;">•</span><span>${item}</span></div>`).join('');
    return `${header}<div class="tooltip-scrollable-content">${listItems}</div>`;
  }

  buildTooltipHeader(pureName: string): string {
    const names = pureName.split('<br />');
    const ids = this.normalizedProfessionIds;

    if (names.length > 1 && ids.length > 1) {
      return names.map((name, idx) => `${name} (${ids[idx] || ids[ids.length - 1]})`).join('<br />');
    }

    return `${pureName} (${ids.join(', ') || this.id})`;
  }

  getPureName(title: string): string {
    return title;
  }

  get onClickExcel() {
    const objectParams = {
      id: this.id,
      sid: this.languageSid,
      jobnews_type: 1,
      xls: 1,
    } as any;
    const params = new URLSearchParams(objectParams).toString();
    return `https://api.job-server.net/v1/jobnews/job_list?${params}`;
  }

  searchProfession() {
    ProfessionModule.SetParamsDefault();
    ProfessionModule.SetProfessionId(this.id);
    ProfessionModule.SetProfessionTitle(this.title);
    ProfessionModule.SetJobType(JobType.items);
    AppModule.SetVisibleJobs(true);
  }

  searchProfessionMap() {
    ProfessionModule.SetParamsDefault();
    MapModule.SetProfessionMapId(this.id);
    ProfessionModule.SetProfessionTitle(this.title);
    ProfessionModule.SetJobType(JobType.map);
    AppModule.SetVisibleMap(true);
  }
}
</script>

<style scoped lang="scss">
.profession-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 68px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0 20px;
  margin-bottom: 8px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--aba-primary);
  }
}

.profession-card__main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.profession-card__top {
  flex-shrink: 0;
  color: var(--aba-primary);
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 700;
  font-size: 15px;
  line-height: 1.5;
}

.profession-card__bars {
  display: inline-flex;
  flex-shrink: 0;
  line-height: 0;
}

.profession-card__title {
  min-width: 0;
  color: #1f2937;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profession-card__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.profession-card__warn {
  display: inline-flex;
  color: #dc3545;
  cursor: pointer;
}

.profession-card__btn-group {
  display: flex;
  align-items: stretch;
  height: 38px;
}

.profession-card__count {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 38px;
  padding: 0;
  border: 1px solid #d0d5dd;
  border-right: 0;
  border-radius: 6px 0 0 6px;
  background: #fff;
  color: #1f2937;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }

  &:focus {
    outline: none;
  }
}

.profession-card__menu {
  display: flex;

  ::v-deep .profession-card__chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 38px;
    padding: 0;
    border: 1px solid #d0d5dd;
    border-radius: 0 6px 6px 0;
    background: #f9fafb;
    color: #667085;
    box-shadow: none;
    transition: background-color 0.15s ease;

    &:hover,
    &:focus,
    &:active {
      background: #e5e7eb;
      box-shadow: none;
    }

    .lucide-chevron-down {
      width: 14px;
      height: 14px;
    }
  }
}
</style>

<style>
.profession-info-tooltip .tooltip-inner {
  background-color: #1e293b !important;
  color: #ffffff !important;
  text-align: left !important;
  padding: 16px !important;
  border-radius: 8px !important;
  max-width: 450px !important;
  max-height: 300px !important;
  font-size: 13px !important;
  line-height: 1.4 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
}

.profession-info-tooltip .tooltip-scrollable-content {
  max-height: 250px;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 8px;
}

.profession-info-tooltip .tooltip-scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.profession-info-tooltip .tooltip-scrollable-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.profession-info-tooltip .tooltip-scrollable-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.profession-info-tooltip .tooltip-scrollable-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

.profession-info-tooltip .arrow::before {
  border-left-color: #1e293b !important;
}

.profession-info-tooltip.bs-tooltip-right .arrow::before {
  border-right-color: #1e293b !important;
}

.profession-info-tooltip.bs-tooltip-top .arrow::before {
  border-top-color: #1e293b !important;
}

.profession-info-tooltip.bs-tooltip-bottom .arrow::before {
  border-bottom-color: #1e293b !important;
}
</style>
